import { Rectangle } from "cesium";
import type { Feature, FeatureCollection } from "geojson";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { throttle, useGlobalState } from "senf-shared";
import type { Project } from "senf-shared/types";
import CesiumLoadingBackground from "../../../../../../../assets/logo/CesiumLoadingBackground";
import { useIsMobile } from "../../../../../../../hooks";
import Button from "../../../../../../core/button/Button";
import { useCesiumGis } from "../../../../hooks/useCesiumGis";
import useIdeaPinCesium from "../../../../hooks/useIdeaPinCesium";
import { calculateBoundingBox } from "../../../../utils/calculateBoundingBox";
import {
  flyToPosition,
  initializeCesium,
  loadAndZoomToAsset,
  loadSmartTilesets,
  reapplyGeoidCorrectionToAllTilesets,
  removeAssetById,
  setOsmBuildingsColor,
  toggleOsmBuildings,
} from "../cesium";
import type { GisDataItem, ImageryStyle } from "../cesium-types";

// Generate unique container ID for each Cesium instance
let cesiumInstanceCounter = 0;
function generateContainerId(): string {
  return `cesiumContainer_${++cesiumInstanceCounter}_${Date.now()}`;
}

// Function to estimate memory usage on mobile devices where performance.memory is not available
function getEstimatedMemoryUsage(viewer: any): { used: number; total: number } {
  try {
    // Get device memory if available (Chrome/Edge)
    const deviceMemory = (navigator as any).deviceMemory || 2; // Default to 2GB
    const totalMemoryMB = deviceMemory * 1024; // Convert to MB

    // Estimate used memory based on Cesium scene complexity
    let estimatedUsed = 0;

    if (viewer && viewer.scene) {
      const scene = viewer.scene;

      // Base memory for Cesium viewer
      estimatedUsed += 50; // ~50MB base

      // Add memory for loaded tilesets
      if (scene.primitives) {
        const tilesets = scene.primitives._primitives.filter(
          (p: any) => p._url,
        );
        estimatedUsed += tilesets.length * 30; // ~30MB per tileset
      }

      // Add memory for imagery layers
      if (scene.imageryLayers) {
        estimatedUsed += scene.imageryLayers.length * 20; // ~20MB per imagery layer
      }

      // Add memory for terrain
      if (scene.terrainProvider) {
        estimatedUsed += 40; // ~40MB for terrain
      }

      // Add memory for entities
      if (scene.entities) {
        estimatedUsed += Math.min(scene.entities.values.length * 0.1, 20); // ~0.1MB per entity, max 20MB
      }
    }

    // Cap the estimated usage to reasonable limits
    const maxEstimated = Math.min(totalMemoryMB * 0.3, 512); // Max 30% of device memory or 512MB
    estimatedUsed = Math.min(estimatedUsed, maxEstimated);

    return {
      used: Math.round(estimatedUsed),
      total: totalMemoryMB,
    };
  } catch (error) {
    console.warn("Error estimating memory usage:", error);
    return { used: 0, total: 0 };
  }
}

interface BimModel {
  assetId: number;
  assetName: string;
  detectedEpsg: string;
  fileSize: string;
  uploadDate: Date;
  styles?: StyleCondition[];
}

interface StyleCondition {
  property: string;
  value: string;
  color: string;
  opacity: number;
}

export interface CesiumProps {
  bimAssetIds?: number[];
  bimModels?: BimModel[];
  mapStyle?: string;
  onCesiumCameraMove?: (settings: any) => void;
  cesiumCameraPosition?: {
    center: [number, number];
    height: number;
    bearing?: number;
    pitch?: number;
  };
  shouldFlyTo?: {
    center: [number, number];
    height: number;
    bearing?: number;
    pitch?: number;
  };
  imageryStyle?: ImageryStyle;
  polygonDrawingEnabled?: boolean;
  onReady?: () => void;
  enableCameraBoundsRestriction?: boolean;
  preserveDrawingBuffer?: boolean;
  [key: string]: any; // For other props that might be passed
  gisData?: GisDataItem[];
  onGisFeatureClick?: (properties: any) => void;
  project?: Project;
  mapId?: string;
  handleClickIdeaMarker?: (ideaId: string) => void;
}

// Helper function to convert camera position format
function convertCameraPosition(position?: {
  center: [number, number];
  height: number;
  bearing?: number;
  pitch?: number;
}) {
  if (
    !position ||
    !position.center ||
    !Array.isArray(position.center) ||
    position.center.length < 2
  ) {
    return undefined;
  }

  return {
    center: position.center,
    height: position.height,
    bearing: position.bearing || 0,
    pitch: position.pitch || -45,
  };
}

function Cesium({
  bimModels,
  onCesiumCameraMove,
  cesiumCameraPosition,
  shouldFlyTo,
  imageryStyle,
  polygonDrawingEnabled,
  onReady,
  gisData,
  pinData,
  onGisFeatureClick,
  mapData,
  project,
  handleClickIdeaMarker,
  enableBimHoverHighlight,
  mapbounds,
  enableCameraBoundsRestriction,
  preserveDrawingBuffer,
  ...otherProps
}: CesiumProps) {
  const isMobile = useIsMobile();
  const initialized = useRef(false);
  const prevAssetIdsRef = useRef<number[]>([]);
  const cesiumCameraPositionRef = useRef(cesiumCameraPosition);
  const lastShouldFlyToRef = useRef<any>(null);
  const containerIdRef = useRef<string>(generateContainerId());

  // Throttled flyTo function to prevent excessive 3D camera animations
  const throttledFlyToPosition = useMemo(
    () =>
      throttle((viewer: any, flyToSettings: any, cesiumCameraPosition: any) => {
        flyToPosition(viewer, flyToSettings, cesiumCameraPosition);
      }, 500), // Throttle to max 2 call per second (duration of animation)
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [, setStatefulCesiumMap] = useGlobalState().statefulCesiumMap;
  const viewerRef = useRef<any>(null);
  const [viewerReady, setViewerReady] = useState(false);
  // const [fps, setFps] = useState(0);
  // const [lastRenderTime, setLastRenderTime] = useState("");
  // const [memoryUsage, setMemoryUsage] = useState({
  //   used: 0,
  //   total: 0,
  //   available: false,
  // });

  const bimAssetIds = useMemo(() => {
    return mapData?.bimModels?.map((model) => model.assetId) || [];
  }, [mapData?.bimModels]);

  useEffect(() => {
    if (!viewerReady) return;
    const viewer = viewerRef.current;
    if (!viewer) return;
    if (!mapData) return;

    toggleOsmBuildings(viewer, !!mapData?.osmBuildingsEnabled, mapData);
  }, [viewerReady, mapData]);

  // Performance monitoring
  // useEffect(() => {
  //   if (!viewerReady || !viewerRef.current) return;

  //   const viewer = viewerRef.current;
  //   const scene = viewer.scene;

  //   // Enable FPS display
  //   scene.debugShowFramesPerSecond = true;

  //   // Track render time and memory usage
  //   const postRenderHandler = () => {
  //     // Get current time for render timestamp
  //     const now = new Date();
  //     const timeString = `${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}:${now.getMilliseconds()}`;
  //     setLastRenderTime(timeString);

  //     // Calculate FPS from frame rate
  //     const frameRate = scene.frameRate;
  //     if (frameRate) {
  //       setFps(Math.round(frameRate));
  //     }

  //     // Track memory usage
  //     if ("memory" in performance) {
  //       const memory = (performance as any).memory;
  //       setMemoryUsage({
  //         used: Math.round(memory.usedJSHeapSize / 1024 / 1024), // MB
  //         total: Math.round(memory.totalJSHeapSize / 1024 / 1024), // MB
  //         available: true,
  //       });
  //     } else {
  //       // Fallback for mobile devices where performance.memory is not available
  //       // Use WebGL context info and device memory estimation
  //       const estimatedMemory = getEstimatedMemoryUsage(viewer);
  //       setMemoryUsage({
  //         used: estimatedMemory.used,
  //         total: estimatedMemory.total,
  //         available: false,
  //       });
  //     }
  //   };

  //   scene.postRender.addEventListener(postRenderHandler);

  //   return () => {
  //     scene.postRender.removeEventListener(postRenderHandler);
  //   };
  // }, [viewerReady]);

  // Watch for changes in geoidHeightDifference and re-apply correction
  useEffect(() => {
    if (!viewerReady || !viewerRef.current) return;

    const viewer = viewerRef.current;
    const geoidHeightDifference = mapData?.geoidHeightDifference;
    if (geoidHeightDifference !== undefined && geoidHeightDifference !== null) {
      reapplyGeoidCorrectionToAllTilesets(geoidHeightDifference, viewer);
    }
  }, [viewerReady, mapData?.geoidHeightDifference]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    if (!mapData?.osmBuildingsEnabled) return;

    setOsmBuildingsColor(
      viewer,
      mapData.osmBuildingColor,
      mapData.osmBuildingOpacity,
    );
  }, [
    mapData?.osmBuildingsEnabled,
    mapData?.osmBuildingColor,
    mapData?.osmBuildingOpacity,
  ]);

  const bimAssetStyles = useMemo(() => {
    const styles: StyleCondition[] = [];
    if (bimModels) {
      bimModels.forEach((model) => {
        if (model.styles) {
          styles.push(...model.styles);
        }
      });
    }
    return styles;
  }, [bimModels]);

  const reloadAssets = useCallback(() => {
    console.log("reloadAssets", viewerRef.current, bimAssetIds);
    if (!viewerRef.current || !bimAssetIds || bimAssetIds.length === 0) {
      return;
    }

    // 1. Remove all existing assets specified in bimAssetIds
    setTimeout(() => {
      for (const assetId of bimAssetIds) {
        removeAssetById(assetId, viewerRef.current);
      }
    }, 1500);

    // 2. Re-load assets
    setTimeout(() => {
      loadSmartTilesets(bimAssetIds, bimAssetStyles);
    }, 2000);
  }, [bimAssetIds, bimAssetStyles]);

  const transformedGisData = useMemo(() => {
    if (!gisData) return [];

    const gisDataArray = Array.isArray(gisData) ? gisData : [gisData];

    return gisDataArray
      .map((item: any) => {
        if (item && item.data && item.data.type === "FeatureCollection") {
          return item;
        }

        if (item && item.type === "FeatureCollection") {
          return { data: item, layerName: "Default Layer" };
        }

        if (
          item &&
          typeof item.lat === "number" &&
          typeof item.long === "number"
        ) {
          const feature: Feature = {
            type: "Feature",
            properties: { ...item },
            geometry: {
              type: "Point",
              coordinates:
                item.height !== undefined
                  ? [item.long, item.lat, item.height]
                  : [item.long, item.lat],
            },
          };
          const featureCollection: FeatureCollection = {
            type: "FeatureCollection",
            features: [feature],
          };
          return {
            data: featureCollection,
            layerName: item.title || "Point Feature",
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [gisData]);

  // Use the GIS data hook to display GIS data
  useCesiumGis(viewerReady ? viewerRef.current : null, transformedGisData);

  // Create a placeholder for paddings
  const paddings = useMemo(
    () => ({
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    }),
    [],
  );

  // Call the useIdeaPinCesium hook at the top level of the component
  useIdeaPinCesium({
    cesiumViewer: viewerRef.current,
    pinData,
    setIdeaMarkerColor: () => {}, // Temporary placeholder since we don't need DOM color
    paddings,
    setGisData: () => {},
    normalizedGisData: transformedGisData,
  });

  // Update the ref when cesiumCameraPosition changes
  useEffect(() => {
    cesiumCameraPositionRef.current = cesiumCameraPosition;
  }, [cesiumCameraPosition]);

  useEffect(() => {
    const init = async () => {
      if (!initialized.current) {
        initialized.current = true; // Set flag early

        // Ensure container has proper dimensions before initializing Cesium
        const container = document.getElementById(containerIdRef.current);
        if (!container) {
          console.error("Cesium container not found:", containerIdRef.current);
          initialized.current = false;
          return;
        }

        // Wait for container to have dimensions
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max wait
        while (attempts < maxAttempts) {
          const rect = container.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            break;
          }

          await new Promise((resolve) => setTimeout(resolve, 100));
          attempts++;
        }

        if (attempts >= maxAttempts) {
          console.error(
            "Container never got proper dimensions, aborting Cesium initialization",
          );
          initialized.current = false;
          return;
        }

        const hasInitialCameraPosition = !!cesiumCameraPosition;

        let clippingRectangle: Rectangle | undefined;
        if (project?.location) {
          const outerBbox = calculateBoundingBox(
            project?.location?.latitude,
            project?.location?.longitude,
            200,
          );
          const [minLon, minLat, maxLon, maxLat] = outerBbox;
          clippingRectangle = Rectangle.fromDegrees(
            minLon,
            minLat,
            maxLon,
            maxLat,
          );
          (window as any).cesiumClippingRectangle = clippingRectangle;
        }

        await initializeCesium({
          containerId: containerIdRef.current,
          assetIds: bimAssetIds,
          bimAssetStyles,
          mapData,
          enableBimHoverHighlight,
          enableCameraBoundsRestriction,
          preserveDrawingBuffer,
          initialCameraPosition: convertCameraPosition(cesiumCameraPosition),
          autoZoomToAssets: !hasInitialCameraPosition, // Don't auto-zoom if we have a camera position
          // Pass imagery style from formik
          imageryStyle: imageryStyle || "mapbox-outdoors",
          polygonDrawingEnabled: polygonDrawingEnabled || false,
          clippingRectangle,
          mapbounds,
          onObjectClick: (attributes, cursorPosition) => {
            window.dispatchEvent(
              new CustomEvent("cesiumObjectClick", {
                detail: { attributes, cursorPosition },
              }),
            );
            window.dispatchEvent(
              new CustomEvent("objectClick", {
                detail: { attributes, cursorPosition },
              }),
            );

            if (attributes?.ideaId && handleClickIdeaMarker) {
              handleClickIdeaMarker(attributes.ideaId);
            }
          },
          onCameraMove: onCesiumCameraMove,
          cacheBytes: 1400 * 1024 * 1024,
          maximumCacheOverflowBytes: 200 * 1024 * 1024,
          onReady: (viewer) => {
            setTimeout(() => {
              setIsLoading(false);
            }, 3000);
            onReady?.();
            viewerRef.current = viewer;
            setStatefulCesiumMap((prevState) => ({
              ...prevState,
              viewer: viewer,
            }));
            setViewerReady(true);
          },
          onViewCenterChange: (coordinates) => {
            setStatefulCesiumMap((prevState) => ({
              ...prevState,
              centerCoordinates: coordinates,
            }));
          },
        });

        prevAssetIdsRef.current = bimAssetIds || [];
      }
    };
    init();
  }, [
    bimAssetStyles,
    bimAssetIds,
    cesiumCameraPosition,
    handleClickIdeaMarker,
    imageryStyle,
    onCesiumCameraMove,
    onReady,
    polygonDrawingEnabled,
    project?.location,
    setStatefulCesiumMap,
    enableBimHoverHighlight,
    mapData,
    enableCameraBoundsRestriction,
    mapbounds,
    preserveDrawingBuffer,
  ]); // Run only once on mount

  // Handle shouldFlyTo changes (camera transitions)
  useEffect(() => {
    if (!initialized.current || !shouldFlyTo) return;

    const currentShouldFlyTo = shouldFlyTo;
    const lastShouldFlyTo = lastShouldFlyToRef.current;

    if (
      lastShouldFlyTo !== null &&
      lastShouldFlyTo.center[0] === currentShouldFlyTo.center[0] &&
      lastShouldFlyTo.center[1] === currentShouldFlyTo.center[1] &&
      lastShouldFlyTo.height === currentShouldFlyTo.height
    ) {
      return; // No change in shouldFlyTo, do nothing
    }

    lastShouldFlyToRef.current = currentShouldFlyTo;

    const viewer = viewerRef.current;
    if (viewer) {
      const flyToSettings = {
        ...currentShouldFlyTo,
        bearing: currentShouldFlyTo.bearing ?? 0,
        pitch: currentShouldFlyTo.pitch ?? -45,
      };
      throttledFlyToPosition(
        viewer,
        flyToSettings,
        convertCameraPosition(cesiumCameraPositionRef.current),
      );

      if (isMobile) {
        reloadAssets();
      }
    }
  }, [shouldFlyTo, throttledFlyToPosition, reloadAssets]);

  useEffect(() => {
    if (!initialized.current) return; // Don't run if initialization hasn't started

    const currentIds = bimAssetIds || [];
    const previousIds = prevAssetIdsRef.current || [];

    const newAssetIds = currentIds.filter((id) => !previousIds.includes(id));
    if (newAssetIds.length > 0) {
      for (const assetId of newAssetIds) {
        loadAndZoomToAsset(assetId, undefined, containerIdRef.current); // Pass container ID
      }
    }

    const removedAssetIds = previousIds.filter(
      (id) => !currentIds.includes(id),
    );
    if (removedAssetIds.length > 0) {
      for (const assetId of removedAssetIds) {
        removeAssetById(assetId, containerIdRef.current); // Pass container ID
      }
    }

    prevAssetIdsRef.current = currentIds;
  }, [bimAssetIds]);

  // React to imagery style changes
  useEffect(() => {
    if (!initialized.current) return; // Don't run if initialization hasn't started

    // Add small delay to ensure viewer is fully ready and event listeners are set up
    const timeoutId = setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("imageryStyleChange", {
          detail: { style: imageryStyle || "mapbox-outdoors" },
        }),
      );
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [imageryStyle]);

  // Handle window resize to ensure Cesium viewer resizes properly
  useEffect(() => {
    const handleResize = () => {
      if (viewerRef.current && viewerRef.current.scene) {
        try {
          viewerRef.current.scene.requestRender();
        } catch (error) {
          console.warn("Error during resize render request:", error);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="relative w-full h-full">
      {/* Performance Monitor */}
      {/* {viewerReady && (
        <div className="absolute top-4 right-4 z-[1002] bg-black bg-opacity-75 text-white p-2 rounded text-xs font-mono">
          <div>FPS: {fps}</div>
          <div>
            Memory: {memoryUsage.used}MB / {memoryUsage.total}MB
            {!memoryUsage.available && " (est.)"}
          </div>
          <div>
            Critical:{" "}
            {memoryUsage.total > 0 &&
            memoryUsage.used / memoryUsage.total > 0.95
              ? "true"
              : "false"}
          </div>
          <div>Last Render: {lastRenderTime}</div>
        </div>
      )} */}
      {/* <Button
        className="absolute right-3 top-16 z-[1001] p-2 bg-white border border-black cursor-pointer"
        onClick={reloadAssets}
        leadingIcon="refresh"
      /> */}
      <div
        className={`absolute w-full h-full bg-surface-subdued flex items-center justify-center z-[1001] transition-opacity duration-500 ${
          isLoading ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.6) 40%, rgba(249,248,247,0.2) 100%)",
            animation: isLoading
              ? "cesium-pulse-white 2s ease-in-out infinite"
              : "none",
          }}
        />
        <style>
          {`
            @keyframes cesium-pulse-white {
              0%, 100% {
                opacity: 0.4;
              }
              50% {
                opacity: 1;
              }
            }
          `}
        </style>

        <CesiumLoadingBackground />
      </div>

      <div id={containerIdRef.current} className="w-full h-full" />
    </div>
  );
}

export default Cesium;
